import { ComputedRef, computed, ref, watch } from '@nuxtjs/composition-api';
import { useQuery } from 'villus';
import { Category } from './categories';
import { useI18n } from './i18n';
import { CategoriesByIdsDocument, LinkedCategoriesDocument, LinkedCategoriesQueryVariables } from '~/graphql/Category';
import { toNonNullable } from '~/utils/collections';

export const ROOT_CATEGORY_ID = '2';

type CategoryNode = Partial<Category>;

/**
 * @description used for rendering list of linked categories slider in the deals page and in the category page
 *
 * @param whitelist - tp query a small list of categories instead of list of all categories
 * @param categoryId - id of the category to query
 * @returns list of all categories with all detailed information about parent one
 */
export function useLinkedCategories(
  whiteList: ComputedRef<string[]> = computed(() => []),
  targetCategory: ComputedRef<string> = computed(() => ROOT_CATEGORY_ID)
) {
  const categories = ref<CategoryNode[]>([]);

  const parentCategory = ref<CategoryNode | null>(null);

  /**
   *
   * @description only fetch the categories if there is parent id and a list of categories ids to prevent getting massive amount of categories
   * @param parentId parent category id
   */
  const { data } = useQuery({
    query: LinkedCategoriesDocument,
    variables: computed<LinkedCategoriesQueryVariables>(() => ({
      input: {
        ...(whiteList.value.length ? { ids: { in: whiteList.value } } : {}),
        ...(targetCategory.value
          ? {
              parent_id: {
                eq: targetCategory.value.toString(),
              },
            }
          : {}),
      },
      parentCategory: targetCategory.value?.toString() || '',
    })),
    fetchOnMount: computed(() => !!targetCategory.value).value,
    cachePolicy: 'network-only',
  });

  /**
   *
   * @description incase any outer consumers functions change the selected category parent id
   * #1 update the selected parent id to the new one
   * #2 update the query params to that new selected category parent id
   *
   * if there is no parent category selected ( that means that we want to clear all the selected category ), clear that from the query params , update the selected category variables
   */

  watch(data, value => {
    /**
     * @description watch for the fetched selected categories for the slider
     * #1 if there is a preselected category , update that category with the one from the server named as parent category
     *
     * #2 update the category list slider with the categories from the server
     *
     * #3 update the selected category children with the current selected category from query params if there is one
     *
     */
    if (value) {
      categories.value = [
        ...toNonNullable(value?.categories?.items).map(c => ({
          id: c?.id ?? 0,
          name: c?.name || '',
          url_key: c?.url_key || '',
          url: c?.url || '',
          path: c?.path?.split('/').slice(2).join('/'), // filter out the first two levels
          image: c?.image || '',
          children: (c.children as Category['children']) || [],
          level: c?.level ?? 0,
        })),
      ];

      parentCategory.value = value?.parentCategory?.items?.[0] as Category;
    }
  });

  return {
    parentCategory,
    categories,
  };
}

export function useCategorySlider(id?: string) {
  const category = ref<Category | null | undefined>(null);
  const parent = ref<string | null>(id || null);
  const { t } = useI18n();

  watch(category, v => {
    if (v?.children?.length) {
      parent.value = v.id?.toString() || null;
      return;
    }

    const categoryPath = v?.path?.split('/');
    if (categoryPath && categoryPath.length > 1) {
      parent.value = categoryPath[categoryPath.length - 2];
      return;
    }

    return null;
  });

  const { categories: sliderCategories, parentCategory } = useLinkedCategories(
    computed(() => []),
    computed(() => parent.value ?? ROOT_CATEGORY_ID)
  );

  return {
    category,
    parentCategory,
    categories: computed<CategoryNode[] | []>(() =>
      [
        {
          id: parentCategory.value?.id,
          name: parentCategory.value && resolveCategoryName(t)(parentCategory.value),
          url_key: parentCategory.value?.url_key || '',
          url: parentCategory.value?.url || '',
          path: parentCategory.value?.path?.split('/').slice(2).join('/'), // filter out the first two levels
          image: parentCategory.value?.image || '',
          children: (parentCategory.value?.children as Category['children']) || [],
          level: parentCategory.value?.level ?? 0,
        },
        ...sliderCategories.value,
      ].filter(c => c.id)
    ),
  };
}

export function useGetCategoriesByIds(ids: ComputedRef<string[]>) {
  const categories = ref<CategoryNode[] | []>([]);

  const { data } = useQuery({
    query: CategoriesByIdsDocument,
    variables: computed(() => ({ ids: ids.value })),
  });

  watch(data, newData => {
    categories.value =
      newData?.categories?.items?.map(c => ({
        id: c?.id ?? 0,
        name: c?.name || '',
        url_key: c?.url_key || '',
        url: c?.url || '',
        path: c?.path?.split('/').slice(2).join('/'), // filter out the first two levels
        image: c?.image || '',
        level: c?.level ?? 0,
        children: (c?.children as Category['children']) || [],
      })) || [];
  });

  return {
    categories,
  };
}

function resolveCategoryName(t: ReturnType<typeof useI18n>['t']) {
  return (category: CategoryNode): string => {
    if (category?.id?.toString() === ROOT_CATEGORY_ID) {
      return `${t('all')} ${t('categories')}`;
    }

    return `${t('all')} ${category?.name}`;
  };
}
